import React, { useMemo } from 'react';
import { Badge } from '@fluentui/react-components';
import { CheckmarkCircleFilled, WarningFilled } from '@fluentui/react-icons';
import { usePdmAssignmentStyles } from '../../styles/PdmAssignmentPage.styles';
import { countUnassigned } from './CoverageBanner';
import type { PartnerAssignmentRow } from '../../models/pdmAssignment';

interface CoveragePillProps {
  rows: PartnerAssignmentRow[];
  onShowUnassigned: () => void;
}

/**
 * Compact coverage indicator shown next to the partner count. Reads
 * "Full coverage" when every partner has a PDM, otherwise "{n} unassigned"
 * rendered as a button that filters the table down to the unassigned partners.
 *
 * Coverage reflects the full data set (like the CoverageBanner), not the
 * currently filtered view (FR-4/FR-10).
 */
export const CoveragePill: React.FC<CoveragePillProps> = ({ rows, onShowUnassigned }) => {
  const styles = usePdmAssignmentStyles();
  const unassigned = useMemo(() => countUnassigned(rows), [rows]);

  if (rows.length === 0) return null;

  if (unassigned === 0) {
    return (
      <Badge appearance="tint" color="success" icon={<CheckmarkCircleFilled />}>
        Full coverage
      </Badge>
    );
  }

  const label = `${unassigned} unassigned`;

  return (
    <button
      type="button"
      className={styles.coveragePillButton}
      onClick={onShowUnassigned}
      aria-label={`Filter to the ${unassigned} ${unassigned === 1 ? 'partner' : 'partners'} without a PDM`}
    >
      <Badge appearance="tint" color="warning" icon={<WarningFilled />}>
        {label}
      </Badge>
    </button>
  );
};
